import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { Flag } from '../model/flag';
import { FlagService } from 'src/app/core/service/flag.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { NGXLogger } from 'ngx-logger';
import { Subscription, forkJoin } from 'rxjs';
import { MessengerService } from 'src/app/core/service/messenger.service';
import { sortBy } from 'lodash';
import { Code } from 'src/app/core/model/dictionary';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { SettingService } from 'src/app/core/service/setting.service';

@Component({
  standalone: false,
  selector: 'app-flag-list',
  templateUrl: './flag-list.component.html',
  styleUrls: ['./flag-list.component.scss']
})
export class FlagListComponent implements OnInit, OnDestroy
{
  @Input() public memberId: number;
  @Input() public caseId: number;
  @Input() public taskIconAlwaysVisible = false;

  public flagOpened = false;
  public flags: Flag[] = [];
  public selectedFlag: any;
  public flagCategories: Code[];
  public patientLabel = {};

  private flagMessageSubscription: Subscription;

  constructor(
    private flagService: FlagService,
    private logger: NGXLogger,
    private messageHandlerService: MessageHandlerService,
    private messengerService: MessengerService,
    private dictionaryService: DictionaryService,
    private settingService: SettingService
  )
  {
    this.dictionaryService.getCachedFlagCategories().subscribe({
      next: (data) =>
      {
        this.flagCategories = sortBy(data, ['id']);
      }
    });

    this.flagMessageSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message.type === 'reloadMemberFlags')
        {
          this.getFlags();
        }
      }
    });
  }

  /**
   * Check if the Flags is within a member context.
   *
   * @returns boolean Returns true if Flags are for the Member context.
   */
  public get isMemberContext(): boolean
  {
    return (this.memberId !== undefined);
  }

  public ngOnInit(): void
  {
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };
      }
    });
    this.getFlags();
  }

  public ngOnDestroy(): void
  {
    if (this.flagMessageSubscription !== undefined) { this.flagMessageSubscription.unsubscribe(); }
  }

  public openFlagDialog(value: any = null): void
  {
    if (!value)
    {
      value = new Flag();
      this.selectedFlag = value;
      this.flagOpened = true;
    }
    else
    {
      this.flagService.getFlagById(value.id).subscribe({
        next: (data) =>
        {
          this.selectedFlag = data;
          this.flagOpened = true;
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
  }

  public saveChangesHandler(flags: Flag[]): void
  {
    if (flags.length === 1)
    {
      const isNew = flags[0].id === null;
      this.flagService.saveFlag(flags[0], isNew, flags[0].id).subscribe({
        next: () =>
        {
          this.messageHandlerService.displaySuccess('FLAG-SAVED-SUCCESSFULLY');
          this.getFlags();
        },
        error: (error) =>
        {
          this.logger.error(error);
          this.messageHandlerService.displayError(error);
        }
      });
    }
    else
    {
      const saveQueries: any = [];
      flags.forEach((x: Flag) =>
      {
        saveQueries.push(this.flagService.saveFlag(x, true, x.id));
      });
      forkJoin(saveQueries).subscribe({
        complete: () =>
        {
          this.messageHandlerService.displaySuccess('FLAGS-SAVED-SUCCESSFULLY');
          this.getFlags();
        },
        error: (error) =>
        {
          this.logger.error(error);
          this.messageHandlerService.displayError(error);
        },
      });
    }
    this.selectedFlag = undefined;
  }

  public deleteHandler(flag: Flag): void
  {
    if (flag.id !== null)
    {
      this.flagService.deleteFlag(flag.id).subscribe({
        next: () =>
        {
          this.messageHandlerService.displaySuccess('FLAG-DELETED-SUCCESSFULLY');
          this.getFlags();
        },
        error: (error) =>
        {
          this.logger.error(error);
          this.messageHandlerService.displayError(error);
        }
      });
    }
    this.selectedFlag = undefined;
  }

  public cancelChangesHandler(): void
  {
    this.selectedFlag = undefined;
  }

  private getFlags(): void
  {
    if (this.memberId !== undefined)
    {
      this.flagService.getFlagsForMember(this.memberId).subscribe({
        next: (data) =>
        {
          this.flags = sortBy(data, 'priority.id', ['asc']);
        },
        error: (error) =>
        {
          this.logger.error(error);
          this.messageHandlerService.displayError(error);
        }
      });
    }
    else if (this.caseId !== undefined)
    {
      this.flagService.getFlagsForCase(this.caseId).subscribe({
        next: (data) =>
        {
          this.flags = sortBy(data, 'priority.id', ['asc']);
        },
        error: (error) =>
        {
          this.logger.error(error);
          this.messageHandlerService.displayError(error);
        }
      });
    }
  }
}
